import React, { Component } from 'react';
import axios from 'axios';
import Alert from '@material-ui/lab/Alert';
import {
  Button,
  CircularProgress,
  TextField,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  NativeSelect,
  Tooltip
} from '@material-ui/core';
import SortByAlphaIcon from '@material-ui/icons/SortByAlpha';
import PeopleIcon from '@material-ui/icons/People';
import ReplayIcon from '@material-ui/icons/Replay';
import { debounce } from 'throttle-debounce';
import { endpoints } from '../../config';
import {
  ICompanySelectionState,
  ICompanyOption,
  ILocationOption,
  newLocationDefault,
  newAddressKeyCode
} from '../../types/CompanySelection.types';
import { sortHandlerAlpha, sortHandlerNumber } from '../../utils/helpers';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { ILanguageStrings } from '../../utils/languages/languages';
import InputAdornment from '@material-ui/core/InputAdornment';
import { IconStyled } from './ProductsServices';
import { isValidEmail, isEmailInDomainList } from '../../utils/validations';
import { EMAIL_DOMAINS_IGNORE } from '../../utils/structures';
const DEFAULT_SELECTED_COMPANY_INDEX = 10000;

interface ICompanySelectionProps {
  nextStep: Function;
  previousStep: Function;
  handleChange: Function;
  data: ICompanySelectionState;
  loadingTagged: boolean;
  languageStrings: ILanguageStrings;
  emailAddress: string;
  isInvited: boolean;
}

interface ICountryOption {
  countrycode: string;
  description: string;
}

interface IStateOption {
  statecode: string;
  description: string;
}

interface ISiteType {
  code: string;
  description: string;
}

interface IState {
  submitting: boolean;
  newLocation: any;
  companyList: [];
  companyListSorted: [];
  shouldRunSupplierSearch: boolean;
  loading: boolean;
  locationLoading: boolean;
  isAddOptionShown: boolean;
  searchText: string;
  highligthSelectCompany: number;
  isModalCompanyNameDuplicatedOpen: boolean;
  isErrorCompanyAlreadyRegistered: boolean;
  isSortAlphaAsc: boolean;
  isSortNumberDesc: boolean;
  notAllowResetFilters: boolean;
  hasDomainSearchResults: boolean;
  loadingDomainSearch: boolean;
}

export class CompanySelection extends Component<ICompanySelectionProps, {}> {
  constructor(props: ICompanySelectionProps) {
    super(props);
    this.handleAutoCompleteSupplierSearch = debounce(500, this.handleAutoCompleteSupplierSearch);
  }

  state: IState = {
    submitting: false,
    newLocation: newLocationDefault,
    companyList: [],
    companyListSorted: [],
    shouldRunSupplierSearch: true,
    loading: false,
    locationLoading: false,
    isAddOptionShown: false,
    searchText: '',
    highligthSelectCompany: DEFAULT_SELECTED_COMPANY_INDEX,
    isModalCompanyNameDuplicatedOpen: false,
    isSortAlphaAsc: false,
    isSortNumberDesc: false,
    notAllowResetFilters: true,
    hasDomainSearchResults: false,
    loadingDomainSearch: false,
    isErrorCompanyAlreadyRegistered: false
  };

  componentDidMount() {
    if (this.props.data.stateProvince === '') {
      const countriesPromise = axios.get(`${endpoints.Countries}`);
      const params = { countrycode: newLocationDefault.country };
      const stateProvincePromise = axios.get(`${endpoints.StateProvince}`, { params });
      const siteTypeCodePromise = axios.get(`${endpoints.SiteType}`);
      Promise.all([countriesPromise, stateProvincePromise, siteTypeCodePromise])
        .then((responses: any) => {
          const countryOptions = { target: { value: responses[0].data } };
          this.handleChange(countryOptions, 'countryOptions');
          const resultStateOptions = { target: { value: responses[1].data } };
          this.handleChange(resultStateOptions, 'stateProvinceOptions');
          const siteTypeCodeOptions = { target: { value: responses[2].data.response } };
          this.handleChange(siteTypeCodeOptions, 'siteTypeCodeOptions');
        })
        .catch(err => err);
    }

    const companyKey = this.props.data.company?.companyKey;
    if (companyKey) {
      axios.get(`${endpoints.CompanyAddresses}?supplierCompanyKey=${companyKey}`).then(response => {
        if (Array.isArray(response.data) && response.data.length > 0) {
          const locations = response.data;
          const targetValueLocations = { target: { value: locations } };
          this.handleChange(targetValueLocations, 'locations');
          this.setState({ locations, locationLoading: false });
          if (this.props.isInvited) {
            //invited user and addresses are found for that company
            const targetValueLocations = { target: { value: { addresskey: newAddressKeyCode } } };
            this.handleChange(targetValueLocations, 'selectLocation');
          }
        } else {
          const targetValueLocations = { target: { value: { addresskey: newAddressKeyCode } } };
          this.handleChange(targetValueLocations, 'selectLocation');
          this.setState({ locationLoading: false });
        }
      });
    }

    const { emailAddress } = this.props;

    if (isValidEmail(emailAddress) && !isEmailInDomainList(emailAddress, EMAIL_DOMAINS_IGNORE)) {
      this.searchCompaniesByUserDomain();
    }
  }

  searchCompaniesByUserDomain() {
    const { isInvited, emailAddress: searchTerm } = this.props;
    const params = { searchTerm };
    if (!isInvited) {
      this.setState({ loadingDomainSearch: true });
      axios
        .get(endpoints.CompanySearchByDomain, { params })
        .then(response => {
          const { searchedDomain, companies } = response.data.response;
          if (companies && companies.length) {
            const companyList: ICompanyOption[] = companies.map((element: any) => ({
              name: element.companyName,
              isExisting: true,
              companyKey: element.companyKey,
              activeUsers: element.activeUsers
            }));

            this.setState({
              searchText: searchedDomain,
              shouldRunSupplierSearch: true,
              loadingDomainSearch: false,
              companyList: companyList,
              highligthSelectCompany: DEFAULT_SELECTED_COMPANY_INDEX,
              hasDomainSearchResults: true
            });

            const textField: any = document.querySelector('#company-selection');
            textField.value = searchedDomain;
          }
        })
        .finally(() => {
          this.setState({ loadingDomainSearch: false });
        });
    }
  }

  handleChange = (event: any, inputName: string) => {
    this.props.handleChange('companySelection', inputName)(event);
  };

  nextStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    let shouldGoToNextStep: boolean = true;

    if (this.state.highligthSelectCompany === DEFAULT_SELECTED_COMPANY_INDEX && this.state.companyList.length > 0) {
      const companyList: ICompanyOption[] = this.state.companyList;
      companyList.forEach(element => {
        if (element.name?.trim().toLowerCase() === this.state.searchText?.trim().toLowerCase()) {
          shouldGoToNextStep = false;
          this.setState({ isModalCompanyNameDuplicatedOpen: true });
        }
      });
    }

    shouldGoToNextStep && this.props.nextStep();
  };

  previousStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    this.props.previousStep();
  };

  clearNewLocation = () => {
    this.setState({ newLocation: newLocationDefault });
  };

  handleCountryChange = (e: { target: { value: string } }, field: string) => {
    this.handleChange(e, field);

    const params = {
      countrycode: e.target.value
    };

    axios.get(`${endpoints.StateProvince}`, { params }).then(response => {
      const resultStateOptions = response.data;
      const stateProvinceOptions = { target: { value: resultStateOptions } };
      this.handleChange(stateProvinceOptions, 'stateProvinceOptions');

      const resetStateProvinceSelection = { target: { value: '' } };
      this.handleChange(resetStateProvinceSelection, 'stateProvince');
    });
  };

  scrollDown = () => {
    window.scroll(0, 600);
    window.scrollTo(window.scrollX, window.scrollY);
  };

  handleAutoCompleteChange = (newValue: any, index: number) => {
    this.setState({ highligthSelectCompany: index });
    if (this.props.data.selectedSupplierCompanyKey?.includes(Number(newValue.companyKey))) {
      const targetValueLocations = { target: { value: [] } };
      this.handleChange(targetValueLocations, 'locations');
      this.handleChange({ target: { value: null } }, 'company');
      this.setState({ isErrorCompanyAlreadyRegistered: true });
    } else {
      if (!newValue.isExisting) {
        newValue.name = newValue.inputValue;
      }
      if (newValue && newValue.name && newValue.companyKey) {
        this.setState({ company: newValue, locationLoading: true, isErrorCompanyAlreadyRegistered: false });
        const newTargetValue = { target: { value: newValue } };
        this.handleChange(newTargetValue, 'company');
        axios
          .get(`${endpoints.CompanyAddresses}?supplierCompanyKey=${newValue.companyKey}`)
          .then(response => {
            if (Array.isArray(response.data) && response.data.length > 0) {
              const locations = response.data;
              const targetValueLocations = { target: { value: locations } };
              this.handleChange(targetValueLocations, 'locations');
              this.setState({ locations, locationLoading: false });
            } else {
              const targetValueLocations = { target: { value: { addresskey: newAddressKeyCode } } };
              this.handleChange(targetValueLocations, 'selectLocation');
              this.setState({ locationLoading: false });
            }
          })
          .catch(() => {
            const targetValueLocations = { target: { value: { addresskey: newAddressKeyCode } } };
            this.handleChange(targetValueLocations, 'selectLocation');
            this.setState({ locationLoading: false });
          });
      } else {
        const targetValueCompany = { target: { value: null } };
        this.handleChange(targetValueCompany, 'company');
        this.clearNewLocation();
      }
      const targetValueCompany = { target: { value: newValue } };
      this.handleChange(targetValueCompany, 'company');
      setTimeout(() => this.scrollDown(), 200);
    }
  };

  handleAutoCompleteSupplierSearch = (ev: { target: { value: string } }) => {
    const newValue = ev.target.value;
    this.setState({ searchText: newValue.trim() });
    if (this.state.shouldRunSupplierSearch && newValue.trim().length >= 2) {
      if (newValue.trim().length === 2) {
        setTimeout(() => {
          this.performCompanySearch(newValue);
        }, 650);
      } else {
        this.performCompanySearch(newValue);
      }
    } else if (newValue.trim().length > 0) {
      this.setState({
        companyList: this.handleAutoCompleteFilter([], { inputValue: newValue })
      });
    } else {
      this.resetSearch();
    }
  };

  performCompanySearch = (searchedValue: string) => {
    this.setState({
      loading: true,
      shouldRunSupplierSearch: false
    });
    let copyArray: ICompanyOption[] = [];
    axios
      .get(`${endpoints.CompanyInfo}/search?companyName=${searchedValue}`)
      .then(res => {
        let companies = res.data.response;
        if (this.state.companyList.length <= 0) {
          if (Array.isArray(companies[0])) {
            companies = companies[0];
          }
        }

        copyArray = companies.map((element: any) => ({
          name: element.companyName,
          isExisting: true,
          companyKey: element.companyKey,
          activeUsers: element.activeUsers
        }));

        copyArray = this.handleAutoCompleteFilter(copyArray, { inputValue: searchedValue });
        this.setState({
          shouldRunSupplierSearch: true,
          loading: false,
          companyList: copyArray,
          highligthSelectCompany: DEFAULT_SELECTED_COMPANY_INDEX
        });
      })
      .catch(err => {
        copyArray = this.handleAutoCompleteFilter(copyArray, { inputValue: searchedValue });
        this.setState({
          shouldRunSupplierSearch: true,
          loading: false,
          companyList: copyArray
        });
      });
  };

  handleSortAlpha = () => {
    this.setState(
      (prevState: IState) => {
        return {
          ...prevState,
          isSortNumberDesc: false,
          isSortAlphaAsc: !prevState.isSortAlphaAsc
        };
      },
      () => {
        const { companyList, companyListSorted: currentSorted, isSortAlphaAsc } = this.state;
        const sortOption = isSortAlphaAsc ? 'asc' : 'desc';
        const listToSort = currentSorted && currentSorted.length ? currentSorted : companyList;
        const companyListSorted = [...listToSort].sort((company1: ICompanyOption, company2: ICompanyOption) =>
          sortHandlerAlpha(company1.name, company2.name, sortOption)
        );
        this.setState({ companyListSorted, notAllowResetFilters: false });
      }
    );
  };

  handleSortNumber = () => {
    this.setState(
      (prevState: IState) => {
        return {
          ...prevState,
          isSortAlphaAsc: false,
          isSortNumberDesc: !prevState.isSortNumberDesc
        };
      },
      () => {
        const { companyList, companyListSorted: currentSorted, isSortNumberDesc } = this.state;
        const sortOption = isSortNumberDesc ? 'desc' : 'asc';
        const listToSort = currentSorted && currentSorted.length ? currentSorted : companyList;
        const companyListSorted = [...listToSort].sort((company1: ICompanyOption, company2: ICompanyOption) =>
          sortHandlerNumber(company1.activeUsers, company2.activeUsers, sortOption)
        );
        this.setState({ companyListSorted, notAllowResetFilters: false });
      }
    );
  };

  handleSortClear = () => {
    this.setState({
      isSortAlphaAsc: false,
      isSortNumberDesc: false,
      companyListSorted: [],
      notAllowResetFilters: true
    });
  };

  resetSearch = (cleanCompanySearch: boolean = true) => {
    this.handleChange({ target: { value: null } }, 'company');
    this.setState({
      companyList: cleanCompanySearch ? [] : this.state.companyList,
      companyListSorted: [],
      isAddOptionShown: false,
      searchText: cleanCompanySearch ? '' : this.state.searchText.trim(),
      hasDomainSearchResults: false,
      isErrorCompanyAlreadyRegistered: false
    });

    const textField: any = document.querySelector(`#company-selection`);
    textField.value = cleanCompanySearch ? null : textField.value.trim();
  };

  handleAutoCompleteFilter = (copyArray: ICompanyOption[], params: any): Array<any> => {
    const companyList: ICompanyOption[] = copyArray;

    if (params.inputValue.trim() !== '') {
      this.setState({ isAddOptionShown: true });
      const filtered = companyList.filter((company: ICompanyOption) => company.isExisting);
      return filtered;
    }

    return companyList;
  };

  handleLocationChange = (e: { target: { value: any } }, location?: ILocationOption) => {
    this.setState({ newLocation: newLocationDefault });
    if (location) {
      const targetValueLocations = { target: { value: location } };
      this.handleChange(targetValueLocations, 'selectLocation');
    } else {
      const targetValueLocations = { target: { value: { addresskey: +e.target.value } } };
      this.handleChange(targetValueLocations, 'selectLocation');
    }
  };

  isContinueDisabled = () => {
    if (this.props.data.company === null || this.state.submitting || this.props.loadingTagged) {
      return true;
    } else if (this.props.data.selectLocation.addresskey === newAddressKeyCode) {
      if (this.props.data.phone && this.props.data.postalcode && this.props.data.city && this.props.data.line1) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  };

  isCompanyExisting = (company: null | ICompanyOption): boolean => {
    if (company !== null) {
      return company.hasOwnProperty('isExisting') ? (company.isExisting as boolean) : true;
    } else {
      return false;
    }
  };

  renderLocationControls = (company: null | ICompanyOption, addresskey: number) => {
    if (addresskey !== null) {
      if (
        (company !== null && addresskey === newAddressKeyCode) ||
        (company !== null && !this.isCompanyExisting(company))
      ) {
        return (
          <div className="location-controls">
            <p>{this.props.languageStrings.companySelection.locationControlsHelpText}</p>

            <div className="location-wrapper">
              <Grid container spacing={3} style={{ width: '100%' }}>
                <Grid item xs={12}>
                  <TextField
                    id="siteName"
                    name="siteName"
                    label={this.props.languageStrings.companySelection.siteName}
                    fullWidth
                    autoComplete="siteName"
                    variant="outlined"
                    onChange={e => this.handleChange(e, 'siteName')}
                    value={this.props.data.siteName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    id="line1"
                    name="address1"
                    label={this.props.languageStrings.companySelection.address1}
                    fullWidth
                    autoComplete="address"
                    variant="outlined"
                    onChange={e => this.handleChange(e, 'line1')}
                    value={this.props.data.line1}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="line2"
                    name="address2"
                    label={this.props.languageStrings.companySelection.address2}
                    fullWidth
                    autoComplete="address line2"
                    variant="outlined"
                    onChange={e => this.handleChange(e, 'line2')}
                    value={this.props.data.line2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NativeSelect
                    required
                    fullWidth
                    autoComplete="country"
                    variant="outlined"
                    data-testid="country"
                    onChange={e => this.handleCountryChange(e, 'country')}
                    placeholder={this.props.languageStrings.companySelection.country}
                    inputProps={{
                      name: 'country',
                      id: 'country'
                    }}
                    value={this.props.data.country}
                    disabled={this.props.data.countryOptions.length === 0}
                  >
                    <option aria-label="None" value="" disabled>
                      {this.props.languageStrings.companySelection.country}
                    </option>
                    {Array.isArray(this.props.data.countryOptions) &&
                      this.props.data.countryOptions.map((country: ICountryOption) => {
                        return (
                          <option key={country.countrycode} value={country.countrycode}>
                            {country.description}
                          </option>
                        );
                      })}
                  </NativeSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    id="city"
                    name="city"
                    label={this.props.languageStrings.companySelection.city}
                    fullWidth
                    variant="outlined"
                    onChange={e => this.handleChange(e, 'city')}
                    value={this.props.data.city}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NativeSelect
                    required
                    fullWidth
                    autoComplete="state-province"
                    variant="outlined"
                    data-testid="stateProvince"
                    onChange={e => this.handleChange(e, 'stateProvince')}
                    placeholder={this.props.languageStrings.companySelection.stateProvince}
                    inputProps={{
                      name: 'stateProvince',
                      id: 'stateProvince'
                    }}
                    disabled={this.props.data.stateProvinceOptions.length === 0}
                    value={this.props.data.stateProvince}
                  >
                    <option aria-label="None" value="" disabled>
                      {this.props.languageStrings.companySelection.stateProvince}
                    </option>
                    {Array.isArray(this.props.data.stateProvinceOptions) &&
                      this.props.data.stateProvinceOptions.map((state: IStateOption) => {
                        return (
                          <option key={state.statecode} value={state.statecode}>
                            {state.description}
                          </option>
                        );
                      })}
                  </NativeSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    id="zip"
                    name="zip"
                    label={this.props.languageStrings.companySelection.zip}
                    fullWidth
                    autoComplete="postal-code"
                    variant="outlined"
                    onChange={e => this.handleChange(e, 'postalcode')}
                    value={this.props.data.postalcode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    id="phoneNumber"
                    name="phoneNumber"
                    label={this.props.languageStrings.companySelection.phoneNumber}
                    fullWidth
                    autoComplete="phone-number"
                    variant="outlined"
                    onChange={e => this.handleChange(e, 'phone')}
                    value={this.props.data.phone}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <NativeSelect
                    required
                    fullWidth
                    autoComplete="site-type-code"
                    variant="outlined"
                    data-testid="siteTypeCode"
                    onChange={e => this.handleChange(e, 'siteTypeCode')}
                    placeholder={this.props.languageStrings.companySelection.siteTypeCode}
                    inputProps={{
                      name: 'siteTypeCode',
                      id: 'site-type-code'
                    }}
                    value={this.props.data.siteTypeCode}
                  >
                    <option aria-label="None" value="" disabled>
                      {this.props.languageStrings.companySelection.siteTypeCode}
                    </option>
                    {Array.isArray(this.props.data.siteTypeCodeOptions) &&
                      this.props.data.siteTypeCodeOptions.map((siteType: ISiteType) => (
                        <option
                          key={`siteType-${siteType.code}`}
                          aria-label={siteType.description}
                          value={siteType.code}
                        >
                          {siteType.description}
                        </option>
                      ))}
                  </NativeSelect>
                </Grid>
              </Grid>
            </div>
          </div>
        );
      } else {
        return '';
      }
    } else {
      return '';
    }
  };

  renderLocationTable = (company: null | ICompanyOption, locations: ILocationOption[], locationLoading: boolean) => {
    if (locationLoading || this.props.loadingTagged) {
      return (
        <>
          <p>{this.props.languageStrings.companySelection.loadingLabel}</p>
          <CircularProgress style={{ width: '24px', height: '24px' }} />
        </>
      );
    } else if ((company !== null && company.isExisting) || this.props.isInvited) {
      if (locations.length === 0) {
        return <p>{this.props.languageStrings.companySelection.tableCompanyNotFound}</p>;
      } else {
        return (
          <>
            <TableContainer component={Paper}>
              <p>{this.props.languageStrings.companySelection.tableTitle}</p>
              <Table aria-label="location table">
                <TableHead>
                  <TableRow>
                    <TableCell>{this.props.languageStrings.companySelection.tableSelect}</TableCell>
                    <TableCell align="center">{this.props.languageStrings.companySelection.tableSiteName}</TableCell>
                    <TableCell align="center">{this.props.languageStrings.companySelection.tableAddress}</TableCell>
                    <TableCell align="center">
                      {this.props.languageStrings.companySelection.tableStateProvince}
                    </TableCell>
                    <TableCell align="center">{this.props.languageStrings.companySelection.tableZip}</TableCell>
                    <TableCell align="center">{this.props.languageStrings.companySelection.country}</TableCell>
                    <TableCell align="center">{this.props.languageStrings.companySelection.tablePhone}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locations.map((location: ILocationOption) => (
                    <TableRow key={location.addresskey}>
                      <TableCell component="th" scope="row">
                        <Radio
                          checked={this.props.data.selectLocation.addresskey === location.addresskey}
                          onChange={e => this.handleLocationChange(e, location)}
                          value={location.addresskey}
                          data-testid={`radio-location-${location.addresskey}`}
                          name="radio-location"
                          inputProps={{ 'aria-label': `Location for ${location.addresskey}` }}
                        />
                      </TableCell>
                      <TableCell align="center">{location.siteName}</TableCell>
                      <TableCell align="center">
                        {location.line1} {location.line2}
                      </TableCell>
                      <TableCell align="center">{location.stateProvince}</TableCell>
                      <TableCell align="center">{location.postalcode}</TableCell>
                      <TableCell align="center">{location.country}</TableCell>
                      <TableCell align="center">{location.phone}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <FormControlLabel
              value="1"
              label={this.props.languageStrings.companySelection.radioLabel}
              control={
                <Radio
                  checked={this.props.data.selectLocation.addresskey === newAddressKeyCode}
                  onChange={e => this.handleLocationChange(e)}
                  value={1}
                  name="radio-location"
                  inputProps={{ 'aria-label': `Add a new location` }}
                />
              }
            />
          </>
        );
      }
    } else {
      return '';
    }
  };

  renderDialogCompanyNameDuplicated() {
    return (
      <Dialog
        open={this.state.isModalCompanyNameDuplicatedOpen}
        onClose={() => this.setState({ isModalCompanyNameDuplicatedOpen: false })}
      >
        <DialogContent>
          <Typography align="center">{this.props.languageStrings.companySelection.companyDuplicatedName}</Typography>
        </DialogContent>
        <DialogActions>
          <Grid container direction="row" justify="space-evenly">
            <Button
              onClick={() => {
                this.setState({ isModalCompanyNameDuplicatedOpen: false });
                this.resetSearch(false);
              }}
              color="primary"
            >
              {this.props.languageStrings.diversity.modalCancelButton}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  }

  renderDomainMatchMessage() {
    const { hasDomainSearchResults } = this.state;
    const { emailAddress } = this.props;
    if (hasDomainSearchResults) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Alert severity="info">
            {this.props.languageStrings.formatString(this.props.languageStrings.companySelection.domainSearchLabel, {
              emailAddress
            })}
          </Alert>
        </div>
      );
    }
  }

  renderCompanyList() {
    const { companyList, companyListSorted } = this.state;
    const listToRender = companyListSorted && companyListSorted.length ? companyListSorted : companyList;
    return listToRender.map((item: ICompanyOption, index: number) => (
      <ListItem
        selected={this.state.highligthSelectCompany === index}
        button
        key={item.name}
        onClick={() => this.handleAutoCompleteChange(item, index)}
      >
        <ListItemText>
          {`${item.name} (${item.activeUsers}`}
          <Tooltip
            placement="top"
            arrow
            title={`${item.activeUsers} ${this.props.languageStrings.companySelection.activeAccountsLabel}`}
          >
            <PeopleIcon className="tooltip-icon" />
          </Tooltip>
          {')'}
        </ListItemText>
      </ListItem>
    ));
  }

  renderSearchControls(isInvited: boolean) {
    if (!isInvited) {
      const { loading } = this.state;
      const sortAlphaTitle = this.props.languageStrings.formatString(
        this.props.languageStrings.companySelection.sortingLabelAlpha,
        {
          sortOption: this.state.isSortAlphaAsc
            ? this.props.languageStrings.companySelection.sortAscending
            : this.props.languageStrings.companySelection.sortDescending
        }
      ) as string;

      const sortUsersTitle = this.props.languageStrings.formatString(
        this.props.languageStrings.companySelection.sortingLabelUsers,
        {
          sortOption: this.state.isSortNumberDesc
            ? this.props.languageStrings.companySelection.sortAscending
            : this.props.languageStrings.companySelection.sortDescending
        }
      ) as string;
      return (
        <>
          {this.renderDomainMatchMessage()}
          <p className="instructions">{this.props.languageStrings.companySelection.helpText}</p>
          <TextField
            id="company-selection"
            label={this.props.languageStrings.companySelection.companySearchLabel}
            data-testid="company-search"
            variant="outlined"
            onChange={ev => this.handleAutoCompleteSupplierSearch(ev)}
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? <CircularProgress color="inherit" size={30} style={{ marginRight: 15 }} /> : null}
                  <IconStyled onClick={() => this.resetSearch()}>
                    <i className="material-icons" title={this.props.languageStrings.productsServices.cancelIconTooltip}>
                      cancel
                    </i>
                  </IconStyled>
                </InputAdornment>
              )
            }}
          />
          <Grid container direction="column" alignItems="center">
            <Grid container item md={3} justify="center" alignContent="flex-start">
              {this.state.companyList && this.state.companyList.length > 0 && (
                <>
                  <div className="Sort-items">
                    <Button
                      variant={this.state.isSortAlphaAsc ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={this.handleSortAlpha}
                      title={sortAlphaTitle}
                    >
                      <SortByAlphaIcon />
                    </Button>
                    <Button
                      variant={this.state.isSortNumberDesc ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={this.handleSortNumber}
                      title={sortUsersTitle}
                    >
                      <PeopleIcon />
                    </Button>
                    <Button
                      disabled={this.state.notAllowResetFilters}
                      variant="outlined"
                      color="primary"
                      onClick={this.handleSortClear}
                      title={this.props.languageStrings.companySelection.sortingLabelRelevancy}
                    >
                      <ReplayIcon />
                    </Button>
                  </div>
                  <List className="Company-List" data-testid="company-selection">
                    {this.renderCompanyList()}
                  </List>
                </>
              )}
            </Grid>
            <Grid container item md={4} justify="center" alignContent="flex-start">
              {this.state.isAddOptionShown && (
                <ListItem
                  button
                  data-testid="add-company"
                  onClick={() =>
                    this.handleAutoCompleteChange(
                      {
                        inputValue: this.state.searchText,
                        name: this.state.searchText,
                        isExisting: false
                      },
                      DEFAULT_SELECTED_COMPANY_INDEX
                    )
                  }
                >
                  <ListItemText>
                    <Typography align="center" color="secondary">
                      {
                        this.props.languageStrings.formatString(
                          this.props.languageStrings.companySelection.companyNotFound,
                          {
                            text: this.state.searchText
                          }
                        ) as string
                      }
                    </Typography>
                  </ListItemText>
                </ListItem>
              )}
            </Grid>
          </Grid>
        </>
      );
    } else {
      return <></>;
    }
  }

  renderContent() {
    const { loadingDomainSearch } = this.state;
    if (loadingDomainSearch) {
      return (
        <div style={{ overflow: 'hidden', marginTop: '20px' }}>
          <CircularProgress />
        </div>
      );
    }
    return (
      <>
        {this.renderSearchControls(this.props.isInvited)}
        {this.renderLocationTable(this.props.data.company, this.props.data.locations, this.state.locationLoading)}
        {this.renderLocationControls(this.props.data.company, this.props.data.selectLocation.addresskey)}
      </>
    );
  }

  render() {
    return (
      <div className="CompanySelection">
        {this.renderDialogCompanyNameDuplicated()}
        <div className="Wrapper">
          {this.renderContent()}

          <div className="Form-actions multiple">
            <Button
              variant="outlined"
              color="primary"
              onClick={e => {
                this.previousStep(e);
              }}
            >
              {this.props.languageStrings.backButtonLabel}
            </Button>

            <Button
              disabled={this.isContinueDisabled()}
              variant="contained"
              color="primary"
              onClick={e => {
                this.nextStep(e);
              }}
            >
              {this.state.submitting || this.props.loadingTagged ? (
                <CircularProgress style={{ width: '24px', height: '24px' }} />
              ) : (
                <span>{this.props.languageStrings.continueButtonLabel}</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default CompanySelection;
